import orgApi from '@api/orgApi';
import roleApi from '@api/roleApi';
import ClaimSelect from '@components/claimSelect';
import ItemEditor from '@components/itemEditor';
import useAuth from '@hooks/useAuth';
import { useTranslator } from '@i18n';
import { InputWrapper, TextInput } from '@mantine/core';
import type { Role } from '@models/role';
import { createFileRoute } from '@tanstack/react-router';
import { createForm } from 'cross-state/react';

export const Route = createFileRoute('/profile/orgs/$orgId/roles/$id')({
  component: RoleEditor,
});

const defaultValue: Role = {
  id: '',
  orgId: '',
  name: '',
  claims: [],
};

const roleForm = createForm<Role>({
  defaultValue,
  validations: {
    orgId: { required: (x) => !!x },
    name: { required: (x) => !!x },
    claims: { required: (x) => x.length > 0 },
  },
});

function RoleEditor() {
  const t = useTranslator();
  const { orgId, id: roleId } = Route.useParams();
  const [org] = orgApi.get(orgId).useCache();
  const { hasClaim } = useAuth();

  return (
    <ItemEditor
      domain="roles"
      api={roleApi}
      readonly={(role) => !hasClaim('orgs/write', !role ? orgId : role.orgId)}
      back={{ to: '/profile/orgs/$id', params: { id: orgId } }}
      form={roleForm}
      name={(role) => role.name}
      formProps={{
        defaultValue: {
          ...defaultValue,
          orgId: orgId ?? '',
        },
      }}
    >
      {() => (
        <>
          <InputWrapper label={t('roles.properties.org.label')}>
            <div>{org?.name ?? orgId}</div>
          </InputWrapper>

          <roleForm.Field
            name="name"
            render={(props) => (
              <TextInput
                {...props}
                label={t('roles.properties.name.label')}
                withAsterisk
                autoComplete="off"
                spellCheck={false}
              />
            )}
          />

          <roleForm.Field
            name="claims"
            render={(props) => (
              <ClaimSelect
                {...props}
                label={t('roles.properties.claims.label')}
                withAsterisk
                orgId={orgId}
                roleId={roleId === 'create' ? undefined : roleId}
              />
            )}
          />
        </>
      )}
    </ItemEditor>
  );
}
